import { useEffect, useState } from "react";
import { useDownloads, type DownloadsController, type Transfer } from "../../controllers/useDownloads";
import { fetchIconUrl } from "../../controllers/downloadsApi";
import {
  formatBytes,
  latestFor,
  PLATFORM_LABEL,
  type DesktopApp,
  type DesktopRelease,
} from "../../models/downloads";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { UserCell, UserDirError, type UserDir } from "../components/UserCell";
import { DesktopAppWizard, PublishReleaseWizard } from "./DownloadsAdmin";

// 다운로드 (자료실): the catalog of downloadable desktop applications. Every signed-in user can
// download any published app — no purchase, no entitlement, unlike the 앱 marketplace next door.
// Platform admins additionally get the publishing surface: register an app, upload an icon, push a
// new version, hide or delete. Deliberately a file repository, not an updater: nothing here is
// installed or refreshed automatically.

export function DownloadsView({ admin }: { admin: boolean }) {
  const c = useDownloads();
  const dir = useUserDirectory(c.getToken);
  const [openAppId, setOpenAppId] = useState<string | null>(null);
  const [wizard, setWizard] = useState<"register" | "edit" | "release" | null>(null);

  const open = c.apps.find((a) => a.id === openAppId) ?? null;
  // The app can vanish under us (another admin deleted it, or a reload dropped an unpublished one).
  useEffect(() => {
    if (openAppId && !c.loading && !open) setOpenAppId(null);
  }, [openAppId, open, c.loading]);

  // Resolve every uploader id to a name — the 올린 사람 column must never render a raw user id.
  useEffect(() => {
    if (!admin || !open) return;
    dir.ensure(open.releases.map((r) => r.uploadedBy).filter((id): id is string => !!id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, open]);

  return (
    <div>
      <div className="dl-head">
        <div>
          <h1 className="ext-title">다운로드</h1>
          <p className="ext-sub">
            데스크톱 앱 설치 파일을 내려받는 자료실입니다. 로그인한 사용자라면 누구나 받을 수 있어요.
          </p>
        </div>
        {admin && !open && (
          <button className="btn primary" style={{ width: "auto" }} onClick={() => setWizard("register")}>
            ＋ 앱 등록
          </button>
        )}
      </div>

      {c.error && (
        <div className="note dl-error" role="alert">
          <span><b>오류:</b> {c.error}</span>
          <span className="dl-error-actions">
            <button className="link-btn" onClick={() => void c.reload()}>다시 시도</button>
            <button className="link-btn" onClick={c.clearError}>닫기</button>
          </span>
        </div>
      )}

      {c.download && <TransferBar transfer={c.download} verb="다운로드 중" />}
      {c.upload && <TransferBar transfer={c.upload} verb="업로드 중" />}

      {c.loading ? (
        <div className="center">불러오는 중…</div>
      ) : open ? (
        <AppDetail
          app={open}
          c={c}
          dir={dir}
          admin={admin}
          onBack={() => setOpenAppId(null)}
          onEdit={() => setWizard("edit")}
          onNewRelease={() => setWizard("release")}
        />
      ) : c.apps.length === 0 && c.error ? (
        // A failed load must not also claim the catalog is empty — "앱이 없습니다" would send the
        // user away believing there is nothing to download. The error banner above is the message.
        null
      ) : c.apps.length === 0 ? (
        <div className="ext-empty">
          {admin
            ? "등록된 앱이 없습니다. 오른쪽 위 ＋ 앱 등록으로 첫 앱을 올려보세요."
            : "아직 내려받을 수 있는 앱이 없습니다."}
        </div>
      ) : (
        <div className="ext-grid">
          {c.apps.map((app) => (
            <AppCard key={app.id} app={app} c={c} onOpen={() => setOpenAppId(app.id)} />
          ))}
        </div>
      )}

      {wizard === "register" && (
        <DesktopAppWizard c={c} onClose={() => setWizard(null)} />
      )}
      {wizard === "edit" && open && (
        <DesktopAppWizard c={c} app={open} onClose={() => setWizard(null)} />
      )}
      {wizard === "release" && open && (
        <PublishReleaseWizard c={c} app={open} onClose={() => setWizard(null)} />
      )}
    </div>
  );
}

/** Live byte counter for an in-flight upload/download, so a large installer never looks frozen. */
function TransferBar({ transfer, verb }: { transfer: Transfer; verb: string }) {
  const pct = transfer.total > 0 ? Math.min(100, Math.round((transfer.sent / transfer.total) * 100)) : 0;
  return (
    <div className="dl-transfer">
      <div className="dl-transfer-label">
        <span>{verb} · {transfer.label}</span>
        <span className="mono">{formatBytes(transfer.sent)} / {formatBytes(transfer.total)} ({pct}%)</span>
      </div>
      <div className="gauge-track"><i style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

/** The app's uploaded icon image, falling back to its emoji mark and then to its initial. */
function AppIcon({ app, getToken, size = 44 }: {
  app: DesktopApp;
  getToken: () => string | null;
  size?: number;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!app.hasIconImage) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    // The icon endpoint needs a bearer token, so it cannot be an <img src>; fetch it as a blob and
    // revoke the object URL when this card unmounts or the app's icon changes.
    void fetchIconUrl(getToken, app.id)
      .then((u) => {
        objectUrl = u;
        if (cancelled) URL.revokeObjectURL(u);
        else setUrl(u);
      })
      .catch(() => setUrl(null));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [app.id, app.hasIconImage, app.updatedAt, getToken]);

  const style = { width: size, height: size, fontSize: Math.round(size / 2) };
  if (url) {
    // An unreadable image (wrong format, truncated upload) must degrade to the emoji/initial mark,
    // not to the browser's broken-image glyph — the catalog still has to look like a catalog.
    return <img className="dl-ico" src={url} alt="" style={style} onError={() => setUrl(null)} />;
  }
  return (
    <span className="dl-ico dl-ico-text" style={style}>
      {app.icon || app.name.slice(0, 1).toUpperCase()}
    </span>
  );
}

function AppCard({ app, c, onOpen }: { app: DesktopApp; c: DownloadsController; onOpen: () => void }) {
  const latest = latestFor(app, c.platform);
  const newest = app.releases[0] ?? null;
  return (
    <button className="ext-card dl-card" onClick={onOpen}>
      <AppIcon app={app} getToken={c.getToken} />
      <span className="ext-name">
        {app.name}
        {!app.published && <span className="dl-chip dl-chip-hidden">비공개</span>}
      </span>
      <span className="ext-desc">{app.description || "설명이 없습니다."}</span>
      {/* The chip and the line next to it must describe the SAME build. Showing the newest version
          number beside another platform's size reads as "v1.1.0 for macOS" when the macOS build is
          actually v1.0.0 — an install-the-wrong-version error, not a cosmetic one. */}
      <span className="dl-card-foot">
        {!newest ? (
          <span className="dl-muted">등록된 설치 파일 없음</span>
        ) : latest ? (
          <>
            <span className="dl-chip">v{latest.version}</span>
            <span className="dl-muted">
              {PLATFORM_LABEL[latest.platform]} · {formatBytes(latest.size)}
              {latest.version !== newest.version
                && ` · 최신 v${newest.version}은 ${PLATFORM_LABEL[newest.platform]}만`}
            </span>
          </>
        ) : (
          <span className="dl-note">
            내 OS({PLATFORM_LABEL[c.platform]})용 빌드 없음 · 최신 v{newest.version}({PLATFORM_LABEL[newest.platform]})
          </span>
        )}
      </span>
    </button>
  );
}

function AppDetail({ app, c, dir, admin, onBack, onEdit, onNewRelease }: {
  app: DesktopApp;
  c: DownloadsController;
  dir: UserDir;
  admin: boolean;
  onBack: () => void;
  onEdit: () => void;
  onNewRelease: () => void;
}) {
  const recommended = latestFor(app, c.platform);
  const busy = c.download !== null;

  // Destructive confirmations state the actual blast radius — how many builds, how much data, and
  // that users lose access — rather than asking "삭제할까요?". These delete the installer bytes too,
  // so a mis-click is not recoverable from the UI.
  async function removeApp() {
    const total = app.releases.reduce((sum, r) => sum + r.size, 0);
    const scope = app.releases.length > 0
      ? `버전 ${app.releases.length}개(설치 파일 ${app.releases.length}개, 총 ${formatBytes(total)})와 배포 이력이 함께 영구 삭제되고, 사용자는 더 이상 이 앱을 받을 수 없습니다.`
      : "등록된 설치 파일은 없습니다.";
    if (!window.confirm(`'${app.name}'을(를) 삭제합니다.\n\n${scope}\n\n되돌릴 수 없습니다. 계속할까요?`)) return;
    if ((await c.removeApp(app.id)).ok) onBack();
  }

  async function removeRelease(r: DesktopRelease) {
    const message = `v${r.version} (${PLATFORM_LABEL[r.platform]}) 설치 파일 '${r.filename}'을(를) 영구 삭제합니다.\n\n`
      + `지금까지 ${r.downloadCount}회 배포됐고, 파일과 배포 이력이 함께 사라집니다.\n\n`
      + "되돌릴 수 없습니다. 계속할까요?";
    if (!window.confirm(message)) return;
    await c.removeRelease(r.id);
  }

  // Hiding pulls the app out of every user's catalog, so it gets a confirmation too — but a soft one,
  // because unlike deletion it is reversible.
  async function togglePublished() {
    if (app.published
      && !window.confirm(`'${app.name}'을(를) 사용자에게 숨깁니다.\n\n`
        + "모든 사용자의 자료실에서 사라지고 다운로드도 막힙니다. 언제든 다시 공개할 수 있습니다.")) {
      return;
    }
    await c.update(app.id, { published: !app.published });
  }

  return (
    <div>
      <button className="link-btn dl-back" onClick={onBack}>← 목록</button>

      <div className="dl-detail-head">
        <AppIcon app={app} getToken={c.getToken} size={64} />
        <div className="dl-detail-main">
          <h2 className="dl-detail-name">
            {app.name}
            {!app.published && <span className="dl-chip dl-chip-hidden">비공개</span>}
          </h2>
          <p className="dl-detail-desc">{app.description || "설명이 없습니다."}</p>
          {recommended ? (
            <div className="dl-cta">
              <button
                className="btn primary"
                style={{ width: "auto" }}
                disabled={busy}
                onClick={() => void c.downloadRelease(recommended)}
              >
                {busy ? "다운로드 중…" : `다운로드 · v${recommended.version} (${PLATFORM_LABEL[recommended.platform]})`}
              </button>
              <span className="dl-muted">{recommended.filename} · {formatBytes(recommended.size)}</span>
            </div>
          ) : app.releases.length === 0 ? (
            // Nothing published yet — do NOT point at the version table below, it is empty too.
            <div className="dl-note">
              아직 등록된 설치 파일이 없습니다.{admin ? " ＋ 새 버전으로 첫 빌드를 올리세요." : " 준비되면 이 자리에 표시됩니다."}
            </div>
          ) : (
            <div className="dl-note">
              현재 내 운영체제({PLATFORM_LABEL[c.platform]})용 설치 파일이 없습니다. 아래 버전 기록에서 다른 빌드를 받으세요.
            </div>
          )}
        </div>
        {admin && (
          <div className="dl-admin-actions">
            <button className="btn" style={{ width: "auto" }} onClick={onNewRelease}>＋ 새 버전</button>
            <button className="btn" style={{ width: "auto" }} onClick={onEdit}>앱 편집</button>
            {/* State and action are separate lines: a lone "비공개로 전환" button reads as the current
                state to about half of people, which is exactly the toggle mislabel 62366 warns about. */}
            <div className="dl-publish-state">
              공개 상태: <b>{app.published ? "공개 중" : "비공개"}</b>
              <span className="dl-muted">
                {app.published ? "로그인한 모든 사용자에게 보입니다" : "관리자만 볼 수 있습니다"}
              </span>
            </div>
            <button className="btn" style={{ width: "auto" }} onClick={() => void togglePublished()}>
              {app.published ? "사용자에게 숨기기" : "공개하기"}
            </button>
            <button className="btn danger" style={{ width: "auto" }} onClick={() => void removeApp()}>앱 삭제</button>
          </div>
        )}
      </div>

      <h3 className="dl-section">버전 기록 · {app.releases.length}</h3>
      {admin && <UserDirError dir={dir} />}
      {/* The admin view adds 받은 수 / 올린 사람, which can outgrow a narrow stage — scroll the table
          rather than let its cells squeeze buttons into vertical slivers. */}
      <div className="dl-table-wrap">
      <table className="sdui-table">
        <thead>
          <tr>
            <th>버전</th>
            <th>플랫폼</th>
            <th>파일</th>
            <th>크기</th>
            <th>배포일</th>
            {admin && <th>받은 수</th>}
            {admin && <th>올린 사람</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {app.releases.map((r) => (
            <tr key={r.id}>
              <td className="dl-ver">
                <b>v{r.version}</b>
                {r.releaseNotes && <div className="dl-notes">{r.releaseNotes}</div>}
              </td>
              <td className="dl-nowrap">{PLATFORM_LABEL[r.platform]}</td>
              <td className="mono dl-filename" title={r.filename}>{r.filename}</td>
              <td className="mono dl-nowrap">{formatBytes(r.size)}</td>
              <td className="mono dl-nowrap">{r.releasedAt.slice(0, 10)}</td>
              {admin && <td className="mono dl-nowrap">{r.downloadCount}</td>}
              {admin && <td className="dl-uploader">{r.uploadedBy ? <UserCell id={r.uploadedBy} dir={dir} /> : "—"}</td>}
              <td>
                <div className="dl-row-actions">
                  <button className="btn" disabled={busy} onClick={() => void c.downloadRelease(r)}>
                    다운로드
                  </button>
                  {/* Separated and styled as destructive: the everyday action and an irreversible
                      file deletion must not be two identical grey buttons 6px apart. */}
                  {admin && (
                    <button className="btn danger" onClick={() => void removeRelease(r)}>삭제</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        {app.releases.length === 0 && (
          <tfoot>
            <tr>
              <td colSpan={admin ? 8 : 6} className="sdui-table-empty">
                아직 등록된 설치 파일이 없습니다.
              </td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
    </div>
  );
}
